import { useState } from 'react'
import { useCreateUser, useDeactivateUser, useEraseUser, useMe, usePatchUser, useUsers } from '../api/hooks'
import type { AdminUser, Role } from '../api/types'
import { Modal } from '../components/Modal'
import { useUi } from '../store/ui'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function UsersTab() {
  const users = useUsers()
  const me = useMe()
  const [showCreate, setShowCreate] = useState(false)
  const [eraseTarget, setEraseTarget] = useState<AdminUser | null>(null)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-ink-600">
          Admins manage settings and AI providers; editors work the translation grid only.
        </p>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + Add user
        </button>
      </div>

      {users.isError && (
        <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900">
          Could not load users: {(users.error as Error).message}
        </p>
      )}

      <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-ink-100 text-2xs uppercase tracking-wider text-ink-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Name</th>
              <th className="px-3 py-2 text-left font-semibold">Email</th>
              <th className="px-3 py-2 text-left font-semibold">Role</th>
              <th className="px-3 py-2 text-left font-semibold">Status</th>
              <th className="px-3 py-2 text-left font-semibold">Created</th>
              <th className="px-3 py-2 text-left font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {(users.data ?? []).map((user) => (
              <UserRow
                key={user.id}
                user={user}
                isSelf={user.id === me.data?.id}
                onRequestErase={() => setEraseTarget(user)}
              />
            ))}
            {users.isSuccess && users.data.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-ink-600">
                  No users yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateUserDialog onClose={() => setShowCreate(false)} />}
      {eraseTarget && <EraseUserDialog user={eraseTarget} onClose={() => setEraseTarget(null)} />}
    </div>
  )
}

function UserRow({
  user,
  isSelf,
  onRequestErase,
}: {
  user: AdminUser
  isSelf: boolean
  onRequestErase: () => void
}) {
  const patch = usePatchUser()
  const deactivate = useDeactivateUser()
  const pushToast = useUi((state) => state.pushToast)
  const [name, setName] = useState(user.name)
  const nameDirty = name !== user.name
  const erased = user.erased_at !== null

  const onRoleChange = (role: Role) => {
    patch.mutate(
      { userId: user.id, role },
      { onError: (error) => pushToast('error', `Could not change role: ${(error as Error).message}`) },
    )
  }

  const saveName = () => {
    patch.mutate(
      { userId: user.id, name: name.trim() },
      {
        onSuccess: () => pushToast('success', `Name updated for ${user.email}.`),
        onError: (error) => pushToast('error', `Could not update name: ${(error as Error).message}`),
      },
    )
  }

  const onToggleActive = () => {
    if (user.is_active) {
      if (!window.confirm(`Deactivate ${user.email}? They will be signed out immediately.`)) return
      deactivate.mutate(user.id, {
        onError: (error) => pushToast('error', `Could not deactivate: ${(error as Error).message}`),
      })
    } else {
      patch.mutate(
        { userId: user.id, is_active: true },
        { onSuccess: () => pushToast('success', `${user.email} reactivated.`) },
      )
    }
  }

  return (
    <tr className={erased ? 'bg-ink-100/60 text-ink-500' : user.is_active ? '' : 'bg-ink-50/60 text-ink-600'}>
      <td className="px-3 py-2">
        {erased ? (
          <span className="italic">{user.name}</span>
        ) : (
          <>
            <label className="sr-only" htmlFor={`name-${user.id}`}>
              Name for {user.email}
            </label>
            <div className="flex items-center gap-1">
              <input
                id={`name-${user.id}`}
                className="field w-32 py-1"
                placeholder={user.email}
                value={name}
                maxLength={200}
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && nameDirty) saveName()
                }}
              />
              {nameDirty && (
                <button className="btn shrink-0" disabled={patch.isPending} onClick={saveName}>
                  Save
                </button>
              )}
            </div>
          </>
        )}
      </td>
      <td className="px-3 py-2 font-mono">{erased ? <span className="italic">{user.email}</span> : user.email}</td>
      <td className="px-3 py-2">
        <label className="sr-only" htmlFor={`role-${user.id}`}>
          Role for {user.email}
        </label>
        <select
          id={`role-${user.id}`}
          className="field w-32 py-1"
          value={user.role}
          disabled={erased || isSelf || patch.isPending}
          title={isSelf ? 'You cannot change your own role' : undefined}
          onChange={(event) => onRoleChange(event.target.value as Role)}
        >
          <option value="admin">Admin</option>
          <option value="editor">Editor</option>
        </select>
      </td>
      <td className="px-3 py-2">
        <span
          className={`chip border ${
            erased
              ? 'border-ink-400 bg-ink-200 text-ink-700'
              : user.is_active
                ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                : 'border-ink-300 bg-ink-100 text-ink-600'
          }`}
        >
          {erased ? 'Erased' : user.is_active ? 'Active' : 'Deactivated'}
        </span>
      </td>
      <td className="px-3 py-2 text-ink-600">{formatDate(user.created_at)}</td>
      <td className="px-3 py-2">
        {erased ? (
          <span className="text-ink-500">Permanently erased</span>
        ) : (
          <div className="flex items-center gap-1.5">
            <button
              className="btn"
              disabled={isSelf || deactivate.isPending || patch.isPending}
              title={isSelf ? 'You cannot deactivate yourself' : undefined}
              onClick={onToggleActive}
            >
              {user.is_active ? 'Deactivate' : 'Reactivate'}
            </button>
            <button
              className="btn btn-danger"
              disabled={isSelf}
              title={isSelf ? 'You cannot erase yourself' : 'Permanently erase this account (irreversible)'}
              onClick={onRequestErase}
            >
              Erase
            </button>
          </div>
        )}
      </td>
    </tr>
  )
}

function EraseUserDialog({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const erase = useEraseUser()
  const pushToast = useUi((state) => state.pushToast)
  const [confirmEmail, setConfirmEmail] = useState('')
  const canConfirm = confirmEmail.trim().toLowerCase() === user.email.toLowerCase()

  const submit = () => {
    if (!canConfirm) return
    erase.mutate(
      { userId: user.id, confirmEmail: confirmEmail.trim() },
      {
        onSuccess: () => {
          pushToast('success', `${user.email} has been permanently erased.`)
          onClose()
        },
        onError: (error) => pushToast('error', `Could not erase: ${(error as Error).message}`),
      },
    )
  }

  return (
    <Modal
      title={`Permanently erase ${user.email}?`}
      description="This is irreversible -- there is no restore path."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-danger" disabled={!canConfirm || erase.isPending} onClick={submit}>
            {erase.isPending ? 'Erasing…' : 'Erase account'}
          </button>
        </>
      }
    >
      <div className="space-y-3 text-xs text-ink-700">
        <ul className="list-disc space-y-1 pl-4">
          <li>The account and their personal data (name, email, password) are permanently removed.</li>
          <li>Their past edits remain, but show as an anonymous former user ("Deleted user") instead of their name.</li>
          <li>Every session they hold is signed out immediately, and any API keys they created stop working.</li>
          <li>This cannot be undone.</li>
        </ul>
        <div>
          <label className="label" htmlFor="erase-confirm-email">
            Type <span className="font-mono">{user.email}</span> to confirm
          </label>
          <input
            id="erase-confirm-email"
            className="field mt-1"
            autoComplete="off"
            spellCheck={false}
            value={confirmEmail}
            onChange={(event) => setConfirmEmail(event.target.value)}
          />
        </div>
        {erase.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-xs text-rose-900">
            {(erase.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  )
}

function CreateUserDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateUser()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<Role>('editor')
  const [name, setName] = useState('')

  const submit = () => {
    create.mutate(
      { email: email.trim().toLowerCase(), password, role, name: name.trim() },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      title="Add user"
      description="They sign in with this email and password immediately; there is no invite email."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!email.trim() || password.length < 8 || create.isPending}
            onClick={submit}
          >
            {create.isPending ? 'Creating…' : 'Create user'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label" htmlFor="new-user-name">
            Name <span className="text-ink-500">(optional)</span>
          </label>
          <input
            id="new-user-name"
            className="field mt-1"
            autoComplete="off"
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="new-user-email">
            Email
          </label>
          <input
            id="new-user-email"
            className="field mt-1"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="new-user-password">
            Password
          </label>
          <input
            id="new-user-password"
            className="field mt-1"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <p className="mt-1 text-2xs text-ink-600">At least 8 characters.</p>
        </div>
        <div>
          <label className="label" htmlFor="new-user-role">
            Role
          </label>
          <select
            id="new-user-role"
            className="field mt-1"
            value={role}
            onChange={(event) => setRole(event.target.value as Role)}
          >
            <option value="editor">Editor</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        {create.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-xs text-rose-900">
            {(create.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  )
}
